import { set } from 'lodash';
import { Suspense, useEffect } from 'react';
import styled, { useTheme } from 'styled-components';

import { IconParagraph, IconText } from '@hivebrite/design-system/Icon';
import { convertRemToPx, type Theme } from '@hivebrite/design-system/Theme';

import TextSettings from 'hb-builder/components/BuilderComponents/single/Text/TextSettings';
import Drop from 'hb-builder/components/common/Drop';
import TextEditor from 'hb-builder/components/common/TextEditor';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { Wrapper, WrapperBuilder } from 'hb-builder/components/common/Wrapper';
import { defaultTabs } from 'hb-builder/constants/tabs';
import {
  useBuilderContext,
  useComponentContext,
  useBuilderConfigContext,
  useRendererContext,
  useDeviceModeContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';
import { loadFont } from 'hb-builder/utils/fonts';
import { getContent } from 'hb-builder/utils/item';
import { shouldDisplayTextComponentAsParagraph } from 'hb-builder/utils/shouldDisplayTextComponentAsParagraph';
import { getCustomFontName } from 'hb-react/backoffice/components/PageBuilder/utils/getCustomFontName';
import type {
  BUILDER_DOCUMENT_TYPE,
  DocumentItem,
} from 'hb-react/backoffice/types/builder/builderDocument';
import type { TextItemAttributes } from 'hb-react/backoffice/types/builder/itemAttributes/textItemAttributes';
import type { FeatureFlags } from 'hb-react/types/featureFlags';

import { SimpleText } from './components/SimpleText';
import { StyledHtml } from './components/StyledHtml';
import { getWrapperTextStyle, headingStyles } from './utils/style';

const componentType = 'single';

const Text: BuilderComponent<TextItemAttributes> = () => <div />; // never used

const RenderBuilder = ({ children }) => {
  const { getPath, updateItem } = useBuilderContext();
  const { item } = useComponentContext();
  const { isMobileMode } = useDeviceModeContext();
  const {
    features: {
      hero: { updateTextBackground: canUpdateTextBackground },
    },
    hivebrite: { networkFont, networkTheming },
  } = useBuilderConfigContext();
  const theme = useTheme();

  useEffect(() => {
    const customFontName = getCustomFontName(networkTheming);

    if (networkFont && networkFont !== customFontName) {
      loadFont(networkFont);
    }
  }, [networkFont]);

  const attributes = item?.attributes as TextItemAttributes;

  const initialValue =
    typeof children === 'string' ? getContent(children) : children;

  if (attributes?.hidden) {
    return null;
  }
  // A component can be `tied` to another one and so cannot be dragged and dropped.
  if (attributes?.tied) {
    return (
      <SimpleText
        attributes={attributes}
        canUpdateTextBackground={canUpdateTextBackground}
        networkFont={networkFont}
        theme={theme}
      >
        {children}
      </SimpleText>
    );
  }

  const handleChangeTextEditor = (value) =>
    updateItem({
      path: getPath(item.id),
      updater: (item) => set(item, 'content', value),
    });

  const textProps = {
    initialValue,
    isDisabled: isMobileMode,
    onChange: handleChangeTextEditor,
  };

  return (
    <Drop accept={['single']}>
      {({ ref: dropRef }) => (
        /* @ts-expect-error untyped */
        <WrapperBuilder
          attributes={attributes}
          dataTestId="builder-text-wrapper"
          ref={dropRef}
          style={getWrapperTextStyle({
            attributes,
            canUpdateTextBackground,
            networkFont,
            theme,
          })}
        >
          {({ isDragging }) =>
            isDragging ? null : (
              <StyledWrapper data-testid="builder-component-text">
                {
                  <Suspense fallback={null}>
                    <StyledTextComponentWrapper>
                      {/* @ts-expect-error untyped */}
                      <TextEditor {...textProps} />
                    </StyledTextComponentWrapper>
                  </Suspense>
                }
              </StyledWrapper>
            )
          }
        </WrapperBuilder>
      )}
    </Drop>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<TextItemAttributes>>) => {
  const { isMobilePreview } = useRendererContext();
  const theme = useTheme();

  const { attributes } = item || {};

  if (attributes?.hidden || !attributes) {
    return null;
  }

  return (
    // @ts-expect-error untyped
    <Wrapper
      attributes={attributes}
      componentType={componentType}
      dataTestId="builder-component-text"
      isMobilePreview={isMobilePreview}
      style={{
        ...getWrapperTextStyle({
          attributes,
          canUpdateTextBackground: true,
          theme,
        }),
        margin: '0',
      }}
    >
      <StyledHtml
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: children }}
      />
    </Wrapper>
  );
};

Text.componentName = 'Text';
Text.componentType = componentType;
Text.componentIcon = ({ config, flags } = {}) =>
  shouldDisplayTextComponentAsParagraph(
    config?.type,
    flags?.flagBuilderResponsiveText,
  )
    ? (props) => <IconParagraph {...props} dataTestId="icon-paragraph" />
    : (props) => <IconText {...props} dataTestId="icon-text" />;

Text.componentLabel = ({ builderType, flags }) =>
  shouldDisplayTextComponentAsParagraph(
    builderType,
    flags?.flagBuilderResponsiveText,
  )
    ? __('Paragraph')
    : __('Text');
Text.componentCategory = 'single';

Text.canBeDoubleClicked = false;
Text.canBeDragged = true;
Text.canBeListed = true;
Text.canBeSelected = true;

interface DefaultConfigArguments {
  theme?: Theme;
  builderType?: BUILDER_DOCUMENT_TYPE;
  featureFlags?: FeatureFlags;
}

const defaultConfig = ({
  theme,
  featureFlags,
  builderType,
}: DefaultConfigArguments) => {
  const defaultFontSize = theme
    ? convertRemToPx(parseInt(theme.fontSizes[2], 10))
    : undefined;

  return {
    containerPadding: '10px 10px 10px 10px',
    content: `<p style="line-height: 22px; text-align: center"><span style="font-size: 14px;">${
      shouldDisplayTextComponentAsParagraph(
        builderType,
        featureFlags?.flagBuilderResponsiveText,
      )
        ? 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. '
        : __('Edit your text with the toolbar above')
    }</span></p>`,
    horizontalAlign: 'left',
    letterSpacing: 0,
    textFontSize: `${defaultFontSize}px`,
  };
};

Text.item = ({ features, featureFlags, theme, builderType }) => {
  const textConfig = {
    ...defaultConfig({ theme, featureFlags, builderType }),
    ...(features?.text || {}),
  };

  return {
    attributes: {
      containerPadding: textConfig.containerPadding,
      defaultTextColor: textConfig.defaultTextColor,
      hidden: textConfig.hidden,
      horizontalAlign: textConfig.horizontalAlign,
      textColor: textConfig.textColor,
      textLetterSpacing: textConfig.letterSpacing,
      textFontFamily: textConfig.fontFamily,
      textFontSize: textConfig.textFontSize,
      tied: textConfig.tied,
    },
    category: Text.componentCategory,
    content: textConfig.content,
    name: Text.componentName,
    type: Text.componentName,
  };
};

Text.toolbar = ['edit', 'drag', 'duplicate', 'delete'];

Text.Settings = TextSettings;
Text.SettingsTabs = defaultTabs;

Text.RenderBuilder = RenderBuilder;
Text.Render = Render;

export default Text;

const StyledTextComponentWrapper = styled.div`
  ${headingStyles}
`;

const StyledWrapper = styled.div`
  width: 100%;
`;
